import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../api/client'
import {
  useAccountDrivers,
  useAccounts,
  useBulkApplyAccountDrivers,
  useCostDrivers,
  useReplaceAccountDrivers,
} from '../api/hooks'
import type { Account, CoverageState } from '../api/types'
import { CoverageBadge, formatPercent } from '../components/Badges'

/**
 * Screen 6 — Account → Driver Mapping (§10).
 *
 * Per account, driver rows with percentages and a footer total that must reach 100.
 * Bulk mode selects several accounts and applies the same map to all of them, which is
 * all-or-nothing on the server: a batch that half-applied would leave the chart in a
 * state nobody chose.
 */

const TOLERANCE = 0.000001

interface Line {
  costDriverId: number
  percent: string
}

const COVERAGE_FILTERS: { value: CoverageState | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'None', label: 'Unmapped' },
  { value: 'Partial', label: 'Partial' },
  { value: 'Complete', label: 'Complete' },
]

export function AccountDriverMapping() {
  const [search, setSearch] = useState('')
  const [coverage, setCoverage] = useState<CoverageState | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)
  const [bulkIds, setBulkIds] = useState<Set<number>>(new Set())
  const [bulkMode, setBulkMode] = useState(false)

  const { data: accounts = [] } = useAccounts(search, coverage)

  // Only allocable accounts can route through a driver.
  const mappable = accounts.filter((a) => a.isAllocable)

  function toggleBulk(id: number) {
    setBulkIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex h-full">
      <section className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
        >
          <h1 className="text-[14px] font-semibold">Account Mapping</h1>

          <input
            className="input w-52"
            placeholder="Search number or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="flex gap-0.5">
            {COVERAGE_FILTERS.map((f) => {
              const active = coverage === f.value
              return (
                <button
                  key={f.label}
                  onClick={() => setCoverage(f.value)}
                  className="rounded px-2 py-1 text-[12px]"
                  style={{
                    background: active ? 'var(--color-accent)' : 'transparent',
                    color: active ? '#fff' : 'var(--color-muted)',
                    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-rule)'}`,
                  }}
                >
                  {f.label}
                </button>
              )
            })}
          </div>

          <label className="ml-auto flex items-center gap-1.5 text-[12px]">
            <input
              type="checkbox"
              checked={bulkMode}
              onChange={(e) => {
                setBulkMode(e.target.checked)
                setBulkIds(new Set())
                setSelectedAccount(null)
              }}
            />
            Bulk mode
          </label>
        </div>

        <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
              <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
                {bulkMode && <th className="w-8 px-3 py-1.5" />}
                <th className="col-head px-3 py-1.5">Number</th>
                <th className="col-head px-3 py-1.5">Name</th>
                <th className="col-head px-3 py-1.5">Coverage</th>
                <th className="col-head px-3 py-1.5 text-right">Mapped %</th>
                <th className="col-head px-3 py-1.5 text-right">Drivers</th>
              </tr>
            </thead>
            <tbody>
              {mappable.map((account) => (
                <tr
                  key={account.accountId}
                  className="grid-row border-b"
                  style={{ borderColor: 'var(--color-rule-soft)' }}
                  data-selected={
                    bulkMode
                      ? bulkIds.has(account.accountId)
                      : selectedAccount?.accountId === account.accountId
                  }
                  tabIndex={0}
                  onClick={() =>
                    bulkMode ? toggleBulk(account.accountId) : setSelectedAccount(account)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      bulkMode ? toggleBulk(account.accountId) : setSelectedAccount(account)
                    }
                  }}
                >
                  {bulkMode && (
                    <td className="px-3 py-1">
                      <input
                        type="checkbox"
                        checked={bulkIds.has(account.accountId)}
                        onChange={() => toggleBulk(account.accountId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  <td className="ident px-3 py-1">{account.accountNumber}</td>
                  <td className="px-3 py-1">{account.name}</td>
                  <td className="px-3 py-1">
                    <CoverageBadge
                      state={account.coverageState}
                      percent={account.totalMappedPercent}
                    />
                  </td>
                  <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
                    {formatPercent(account.totalMappedPercent)}
                  </td>
                  <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
                    {account.mappedDriverCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <aside
        className="w-96 shrink-0 overflow-auto border-l"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        {bulkMode ? (
          <MapEditor
            key={`bulk-${bulkIds.size}`}
            title={`Apply to ${bulkIds.size} account${bulkIds.size === 1 ? '' : 's'}`}
            accountIds={[...bulkIds]}
            existingLines={[]}
          />
        ) : selectedAccount ? (
          <AccountMapEditor key={selectedAccount.accountId} account={selectedAccount} />
        ) : (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            Select an account to route it through one or more drivers, or turn on bulk mode to
            apply the same map to several at once.
          </p>
        )}
      </aside>
    </div>
  )
}

/** Loads an account's current map and hands it to the shared editor. */
function AccountMapEditor({ account }: { account: Account }) {
  const { data: maps = [], isLoading } = useAccountDrivers(account.accountId)

  if (isLoading) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Loading…
      </p>
    )
  }

  const current = maps.filter((m) => m.isCurrent)

  return (
    <MapEditor
      title={account.name}
      subtitle={account.accountNumber}
      accountIds={[account.accountId]}
      existingLines={current.map((m) => ({
        costDriverId: m.costDriverId,
        percent: String(m.percent),
      }))}
      existingEffectiveFrom={current[0]?.effectiveFrom}
    />
  )
}

function MapEditor({
  title,
  subtitle,
  accountIds,
  existingLines,
  existingEffectiveFrom,
}: {
  title: string
  subtitle?: string
  accountIds: number[]
  existingLines: Line[]
  existingEffectiveFrom?: string
}) {
  const { data: drivers = [] } = useCostDrivers()
  const [lines, setLines] = useState<Line[]>(existingLines)
  const [effectiveFrom, setEffectiveFrom] = useState(
    existingEffectiveFrom ?? new Date().toISOString().slice(0, 10),
  )
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const replace = useReplaceAccountDrivers()
  const bulk = useBulkApplyAccountDrivers()
  const pending = replace.isPending || bulk.isPending

  useEffect(() => {
    setLines(existingLines)
    // Re-seeding when the selected account changes; the key on the component already
    // remounts it, so this only guards the bulk case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLines.length])

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + (Number.parseFloat(l.percent) || 0), 0),
    [lines],
  )
  const balanced = Math.abs(total - 100) <= TOLERANCE
  const isBulk = accountIds.length > 1

  // A Retired driver cannot be mapped: QC-A004 would block any run using it.
  const selectable = drivers.filter(
    (d) => d.status !== 'Retired' && !lines.some((l) => l.costDriverId === d.costDriverId),
  )

  function save() {
    setError(null)
    setSaved(null)

    const payload = lines.map((l) => ({
      costDriverId: l.costDriverId,
      percent: Number.parseFloat(l.percent) || 0,
    }))

    const onError = (err: unknown) =>
      setError(err instanceof ApiError ? err.message : String(err))

    if (isBulk) {
      bulk.mutate(
        { accountIds, effectiveFrom, lines: payload },
        {
          onSuccess: (r) => setSaved(`Applied to ${r.accountsUpdated} accounts.`),
          onError,
        },
      )
    } else {
      replace.mutate(
        { accountId: accountIds[0]!, effectiveFrom, lines: payload },
        { onSuccess: () => setSaved('Saved.'), onError },
      )
    }
  }

  if (accountIds.length === 0) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Select one or more accounts to apply a driver map to.
      </p>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-2" style={{ borderColor: 'var(--color-rule-soft)' }}>
        <h2 className="text-[13px] font-semibold">{title}</h2>
        {subtitle && (
          <div className="ident" style={{ color: 'var(--color-faint)' }}>
            {subtitle}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {lines.length === 0 && (
          <p className="mb-3 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            {isBulk
              ? 'Add the drivers to route these accounts through.'
              : 'This account is not routed anywhere. A run against it will be blocked by QC-A001 once it has a balance.'}
          </p>
        )}

        {lines.map((line) => {
          const driver = drivers.find((d) => d.costDriverId === line.costDriverId)
          return (
            <div key={line.costDriverId} className="mb-2 flex items-center gap-2">
              <span className="flex-1 text-[12px]">
                {driver?.name ?? `#${line.costDriverId}`}
                {driver && driver.healthState !== 'Ok' && driver.driverType === 'Static' && (
                  <span
                    className="badge ml-1"
                    style={{
                      background: 'var(--color-error-wash)',
                      color: 'var(--color-error)',
                    }}
                    title="This driver's own allocations do not total 100, so a run using it will be blocked."
                  >
                    {driver.healthState}
                  </span>
                )}
              </span>
              <input
                className="input input-mono w-24 text-right"
                value={line.percent}
                onChange={(e) =>
                  setLines((prev) =>
                    prev.map((l) =>
                      l.costDriverId === line.costDriverId
                        ? { ...l, percent: e.target.value }
                        : l,
                    ),
                  )
                }
                inputMode="decimal"
              />
              <button
                className="text-[11px] underline"
                style={{ color: 'var(--color-muted)' }}
                onClick={() =>
                  setLines((prev) => prev.filter((l) => l.costDriverId !== line.costDriverId))
                }
              >
                remove
              </button>
            </div>
          )
        })}

        {selectable.length > 0 && (
          <select
            className="input mt-2"
            value=""
            onChange={(e) => {
              const id = Number(e.target.value)
              if (id) setLines((prev) => [...prev, { costDriverId: id, percent: '0' }])
            }}
          >
            <option value="">Add a driver…</option>
            {selectable.map((d) => (
              <option key={d.costDriverId} value={d.costDriverId}>
                {d.name} ({d.code})
              </option>
            ))}
          </select>
        )}
      </div>

      <div
        className="flex shrink-0 items-center gap-3 border-t px-4 py-2"
        style={{
          background:
            lines.length === 0
              ? 'var(--color-panel-alt)'
              : balanced
                ? 'var(--color-ok-wash)'
                : 'var(--color-error-wash)',
          borderColor: 'var(--color-rule)',
          color:
            lines.length === 0
              ? 'var(--color-muted)'
              : balanced
                ? 'var(--color-ok)'
                : 'var(--color-error)',
        }}
      >
        <span className="col-head" style={{ color: 'inherit' }}>
          total
        </span>
        <span className="num text-[14px] font-semibold">{formatPercent(total)}</span>
        {lines.length > 0 && !balanced && (
          <span className="text-[11px]">
            {total < 100 ? 'short by' : 'over by'} {formatPercent(Math.abs(100 - total))}
          </span>
        )}
      </div>

      <div className="shrink-0 border-t px-4 py-2" style={{ borderColor: 'var(--color-rule-soft)' }}>
        <label className="mb-2 flex items-center gap-2 text-[12px]">
          <span className="col-head">effective from</span>
          <input
            type="date"
            className="input input-mono w-36"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </label>

        {error && (
          <p className="mb-2 text-[12px]" style={{ color: 'var(--color-error)' }}>
            {error}
          </p>
        )}
        {saved && (
          <p className="mb-2 text-[12px]" style={{ color: 'var(--color-ok)' }}>
            {saved}
          </p>
        )}

        <button
          className="btn btn-primary w-full"
          onClick={save}
          disabled={pending || (lines.length > 0 && !balanced) || (isBulk && lines.length === 0)}
          title={
            lines.length > 0 && !balanced ? 'The legs must total exactly 100.' : undefined
          }
        >
          {pending
            ? 'Saving…'
            : isBulk
              ? `Apply to ${accountIds.length} accounts`
              : lines.length === 0
                ? 'Clear mapping'
                : 'Save mapping'}
        </button>

        {isBulk && (
          <p className="mt-2 text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Applied to every selected account together, or to none of them.
          </p>
        )}
      </div>
    </div>
  )
}
